import React from 'react';
import { motion } from 'framer-motion';
import { SecondaryButton, PrimaryButton } from './buttons';
import { AlertCircle, X } from 'lucide-react';
import { OptimizerApiError } from '../utils/optimizerApi';

interface OptimizerErrorModalProps {
    isOpen: boolean;
    error: OptimizerApiError;
    onClose: () => void;
    onRetry?: () => void;
}

export const OptimizerErrorModal: React.FC<OptimizerErrorModalProps> = ({
    isOpen,
    error,
    onClose,
    onRetry
}) => {
    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="bg-white rounded-lg max-w-lg w-full p-6 relative"
            >
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label="Fechar modal"
                >
                    <X className="w-5 h-5" />
                </button>

                {/* Header */}
                <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                        <AlertCircle className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                        <h3 className="text-xl font-display font-semibold text-gray-900">
                            Erro na Otimização
                        </h3>
                        <p className="text-sm text-gray-600">
                            Falha ao processar dados no otimizador
                        </p>
                    </div>
                </div>

                {/* Error Message */}
                <div className="mb-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                        <p className="text-red-800 font-body mb-2">
                            {error.message}
                        </p>

                        {/* Error Details */}
                        {error.details && error.details.length > 0 && (
                            <div className="mt-3">
                                <p className="text-sm font-medium text-red-800 mb-2">
                                    Detalhes do erro:
                                </p>
                                <ul className="space-y-1">
                                    {error.details.map((detail, index) => (
                                        <li key={index} className="text-sm text-red-700 flex items-start gap-2">
                                            <span className="text-red-500 mt-0.5">•</span>
                                            <span>{detail}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>

                {/* Help Text */}
                <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-800 mb-2">
                        Como resolver:
                    </h4>
                    <ul className="text-sm text-blue-700 space-y-1">
                        <li>• Verifique se todos os vendedores estão cadastrados</li>
                        <li>• Confirme que as cotas somam exatamente 100%</li>
                        <li>• Certifique-se de que os limites de clientes são válidos</li>
                        <li>• Tente novamente em alguns instantes</li>
                    </ul>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                    <SecondaryButton onClick={onClose} className="flex-1">
                        Fechar
                    </SecondaryButton>

                    {onRetry && (
                        <PrimaryButton onClick={onRetry} className="flex-1">
                            Tentar Novamente
                        </PrimaryButton>
                    )}
                </div>
            </motion.div>
        </div>
    );
}; 